import Loading from '@cartwhl/ui/atoms/Loading';
import Text from '@cartwhl/ui/base/Text';
import useMediaQuery from '@cartwhl/ui/hooks/useMediaQuery';
import ChevronLeftIcon from '@cartwhl/ui/icons/ChevronLeftIcon';
import Button from '@cartwhl/ui/input/Button';

interface Props {
  imageURL: string;
  spinnerText: string;
  generationInProgress: boolean;
  onClose: () => void;
  processingPercentage?: number;
}

export function MagnifiedImageView({
  imageURL,
  spinnerText,
  generationInProgress,
  processingPercentage,
  onClose,
}: Props) {
  // Always call both hooks in the same order
  const isWideEnough = useMediaQuery('(min-width: 1200px)');
  const isTallEnough = useMediaQuery('(min-height: 944px)');
  const canShowLarge = isWideEnough && isTallEnough;

  const overlayWidth = canShowLarge ? 280 : 200;
  const paddingY = canShowLarge ? 'pt-8 pb-6' : 'pt-6 pb-4';
  const loaderScale = canShowLarge ? 2 : 1.5;

  return (
    <div className="border-gray-30 dark:border-gray-60 relative h-full w-full overflow-hidden rounded-lg border">
      <img
        src={imageURL}
        crossOrigin="use-credentials"
        className="absolute inset-0 h-full w-full object-cover"
      />

      {!generationInProgress && (
        <div className="absolute top-4 left-4">
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
            icon={<ChevronLeftIcon className="h-6 w-6" />}
          />
        </div>
      )}

      {generationInProgress && (
        <>
          <div className="pointer-events-none absolute inset-0 z-10 bg-gray-200/50" />
          <div className="absolute inset-0 z-10 flex items-center justify-center">
            <div
              className={`dark:bg-gray-90 rounded-2xl bg-white px-4 text-center shadow-none ${paddingY}`}
              style={{ width: overlayWidth }}
            >
              <div className="mb-4 flex justify-center">
                <div style={{ transform: `scale(${loaderScale})` }}>
                  <Loading
                    className="text-green"
                    trackClassName="stroke-gray-30 dark:stroke-gray-60"
                  />
                </div>
              </div>
              <Text variant="studio/body/medium/md" className="dark:text-white">
                {spinnerText} <Percentage value={processingPercentage} />
              </Text>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Percentage({ value }: { value: number | undefined }) {
  if (!value) return <span className="inline-block w-12 text-left"></span>;
  return <span className="inline-block w-12 text-left">{value}%</span>;
}
